import {
  type ReactNode,
  createContext,
  useCallback,
  useRef,
  useState,
} from 'react';

import { StorageKey } from '../constants/Storage';
import { setStorage } from '../utils/localStorage';

export interface ISidebarContextData {
  accordionSidebarValue: string | null;
  collapseSidebar: () => void;
  collapseSubAccordionSidebar: () => void;
  handleAccordionSidebar: (value: string | null) => void;
  handleShrinkSidebar: (isShrunken: boolean) => void;
  handleSubAccordionSidebar: (
    value: string | null,
    accordionSidebarValue?: string | undefined
  ) => void;
  isShrunken: boolean;
  subAccordionSidebarValue: string | null;
  toggleSidebar: () => void;
}

interface ISidebarProps {
  children: ReactNode;
  mainAccordionId?: string;
}

export const SidebarContext = createContext<ISidebarContextData>(
  {} as ISidebarContextData
);

export const SidebarProvider = ({
  mainAccordionId,
  children,
}: ISidebarProps): JSX.Element => {
  const [isShrunken, setIsShrunken] = useState(false);
  const [accordionSidebarValue, setAccordionSidebarValue] = useState<
    string | null
  >(null);
  const [subAccordionSidebarValue, setSubAccordionSidebarValue] = useState<
    string | null
  >(null);

  const accordionSidebarValueRef = useRef<string | null>(null);

  const handleAccordionSidebar = useCallback((value: string | null) => {
    setAccordionSidebarValue(value);
    if (accordionSidebarValueRef.current !== value) {
      setSubAccordionSidebarValue(null);
    }
    accordionSidebarValueRef.current = value;
  }, []);

  const collapseSidebar = useCallback(() => {
    setAccordionSidebarValue(null);
    accordionSidebarValueRef.current = null;
    setSubAccordionSidebarValue(null);
  }, []);

  const handleSubAccordionSidebar = useCallback(
    (value: string | null, accordionSidebarValue = mainAccordionId) => {
      if (accordionSidebarValue) {
        setAccordionSidebarValue(accordionSidebarValue);
        accordionSidebarValueRef.current = accordionSidebarValue;
      }
      setSubAccordionSidebarValue(value);
    },
    [mainAccordionId]
  );

  const collapseSubAccordionSidebar = useCallback(() => {
    setSubAccordionSidebarValue(null);
  }, []);

  const handleShrinkSidebar = useCallback((isShrunken: boolean) => {
    setIsShrunken(isShrunken);
    setStorage<boolean>(StorageKey.SIDEBAR, isShrunken);
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsShrunken(!isShrunken);
    setStorage<boolean>(StorageKey.SIDEBAR, !isShrunken);
  }, [isShrunken]);

  return (
    <SidebarContext.Provider
      value={{
        accordionSidebarValue,
        collapseSidebar,
        collapseSubAccordionSidebar,
        handleAccordionSidebar,
        handleShrinkSidebar,
        handleSubAccordionSidebar,
        isShrunken,
        subAccordionSidebarValue,
        toggleSidebar,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};
